import * as React from 'react';
import type { TranscriptEntry, TranscriptSource, TranscriptSourceContext, TranscriptUtterance } from './types';

/**
 * Stability threshold below which a non-final utterance is treated as
 * speculative (subject to overwrite by a more confident partial). Above the
 * threshold, partial text is considered stable. Sources without a confidence
 * score are treated as confidence === 1.
 */
const STABILITY_THRESHOLD = 0.5;

export type LivePartial = {
	key: string; // `${sourceId}:${partialKey}`
	sourceId: string;
	speaker: TranscriptUtterance['speaker'];
	speakerLabel?: string;
	stableText: string;
	speculativeText: string;
	updatedAt: Date;
};

export type UseTranscriptStreamArgs = {
	/** All sources to mount. Sources are started/stopped together when `enabled` flips. */
	sources: TranscriptSource[];
	/** Master switch; when false the hook tears down all source subscriptions. */
	enabled: boolean;
	/** Scope context passed to each source's `start()`. */
	context: TranscriptSourceContext;
	/**
	 * Called once per finalized utterance. The hook does **not** retain a queue
	 * of finals — the caller is the source of truth for downstream state
	 * (typically the coaching message stream). A stable callback ref is used,
	 * so the consumer doesn't need to memoize.
	 */
	onFinal: (entry: TranscriptEntry) => void;
};

export type UseTranscriptStreamApi = {
	/** Current in-flight partial per `(sourceId, partialKey)`, for live display. */
	livePartials: LivePartial[];
	/** Reset all in-flight partials and the seen-sequence map. */
	clearPartials: () => void;
};

const partialMapKey = (sourceId: string, partialKey: string): string => `${sourceId}:${partialKey}`;

/**
 * Provider-agnostic transcript stream hook.
 *
 * Mounts every source in `sources`, accumulates their utterances, runs:
 *   - per-`(sourceId, partialKey)` out-of-order rejection (when the source
 *     supplies `providerSequenceId`),
 *   - partial → final dedup with stability gating (speculative text is
 *     overwritten by stable text; a `final` event commits the latest
 *     content as a `TranscriptEntry` and clears the in-flight partial),
 *   - speaker-bucket normalization (sources map their wire-level speaker
 *     to `'lead' | 'rep' | 'other'` before reaching here).
 *
 * Finals are pushed out via `onFinal(entry)` rather than buffered, so the
 * downstream coaching layer owns the ordering of "what's been seen."
 *
 * To add a new source: implement `TranscriptSource` and add it to the
 * `sources` array. See `./README.md` for the full recipe.
 */
export function useTranscriptStream({
	sources,
	enabled,
	context,
	onFinal,
}: UseTranscriptStreamArgs): UseTranscriptStreamApi {
	const partialsRef = React.useRef<Map<string, LivePartial>>(new Map());
	const highestSeqRef = React.useRef<Map<string, number>>(new Map());
	const onFinalRef = React.useRef(onFinal);
	const [livePartialsVersion, setLivePartialsVersion] = React.useState(0);

	React.useEffect(() => {
		onFinalRef.current = onFinal;
	}, [onFinal]);

	const handleUtterance = React.useCallback((utterance: TranscriptUtterance) => {
		const key = partialMapKey(utterance.sourceId, utterance.partialKey);

		// Out-of-order rejection (only when the source supplies a sequence id).
		if (typeof utterance.providerSequenceId === 'number') {
			const highest = highestSeqRef.current.get(key) ?? -Infinity;
			if (utterance.providerSequenceId < highest) return;
			highestSeqRef.current.set(key, utterance.providerSequenceId);
		}

		const existing = partialsRef.current.get(key);
		const stable = (utterance.confidence ?? 1) >= STABILITY_THRESHOLD;
		const next: LivePartial = existing
			? { ...existing, updatedAt: utterance.timestamp }
			: {
					key,
					sourceId: utterance.sourceId,
					speaker: utterance.speaker,
					speakerLabel: utterance.speakerLabel,
					stableText: '',
					speculativeText: '',
					updatedAt: utterance.timestamp,
				};

		if (stable) {
			next.stableText = utterance.text;
			next.speculativeText = '';
		} else {
			next.speculativeText = utterance.text;
		}

		if (utterance.isFinal) {
			const finalText = (next.stableText || utterance.text || '').trim();
			partialsRef.current.delete(key);
			if (finalText) {
				const entry: TranscriptEntry = {
					id: `${utterance.sourceId}:${utterance.partialKey}:${utterance.timestamp.getTime()}`,
					sourceId: utterance.sourceId,
					speaker: utterance.speaker,
					speakerLabel: utterance.speakerLabel,
					text: finalText,
					timestamp: utterance.timestamp,
				};
				onFinalRef.current(entry);
			}
		} else {
			partialsRef.current.set(key, next);
		}

		setLivePartialsVersion(v => v + 1);
	}, []);

	// Mount sources whenever (sources, enabled, context.phoneCallId) changes.
	// Each effect run tears down the previous subscriptions before starting
	// new ones to avoid duplicate handlers.
	React.useEffect(() => {
		if (!enabled) return;
		const teardowns = sources.map(source => source.start(handleUtterance, context));
		return () => {
			for (const teardown of teardowns) {
				try {
					teardown();
				} catch (err) {
					console.error('[useTranscriptStream] source teardown failed', err);
				}
			}
		};
		// `context` is intentionally narrowed to its scalar fields for the dep
		// check — re-mounting on the whole object would tear down + restart
		// every render the caller produces a new context literal.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sources, enabled, context.phoneCallId, context.direction, handleUtterance]);

	// When the call ends / hook is disabled, clear in-flight partials so the
	// next call starts clean. Final entries are NOT retained here — the
	// coaching message stream owns the historical record.
	React.useEffect(() => {
		if (enabled) return;
		partialsRef.current = new Map();
		highestSeqRef.current = new Map();
		setLivePartialsVersion(0);
	}, [enabled]);

	const clearPartials = React.useCallback(() => {
		partialsRef.current = new Map();
		highestSeqRef.current = new Map();
		setLivePartialsVersion(v => v + 1);
	}, []);

	const livePartials = React.useMemo(
		() => Array.from(partialsRef.current.values()),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[livePartialsVersion]
	);

	return { livePartials, clearPartials };
}
